import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { AuthProvider } from './auth/AuthProvider'
import { RequireAuth } from './auth/RequireAuth'
import { CompanyProvider } from './companies/CompanyProvider'
import { RequireCompany } from './companies/RequireCompany'
import { AppShell } from './components/AppShell'
import { ImportWizardProvider } from './import/ImportWizardProvider'
import { AccountPage } from './pages/AccountPage'
import { AuditLogPage } from './pages/AuditLogPage'
import { AutomationPage } from './pages/AutomationPage'
import { BackupPage } from './pages/BackupPage'
import { BankStatementsPage } from './pages/BankStatementsPage'
import { CompaniesPage } from './pages/CompaniesPage'
import { DeleteWizardProvider } from './delete/DeleteWizardProvider'
import { DeleteConfirmPage } from './pages/delete/DeleteConfirmPage'
import { DeleteDonePage } from './pages/delete/DeleteDonePage'
import { DeleteFilterPage } from './pages/delete/DeleteFilterPage'
import { DeleteReviewPage } from './pages/delete/DeleteReviewPage'
import { DeleteSelectSourcePage } from './pages/delete/DeleteSelectSourcePage'
import { ExportWizardProvider } from './export/ExportWizardProvider'
import { ExportDonePage } from './pages/export/ExportDonePage'
import { ExportFieldsPage } from './pages/export/ExportFieldsPage'
import { ExportFiltersPage } from './pages/export/ExportFiltersPage'
import { ExportSelectTypePage } from './pages/export/ExportSelectTypePage'
import { HomePage } from './pages/HomePage'
import { JobDetailPage } from './pages/JobDetailPage'
import { JobsListPage } from './pages/JobsListPage'
import { LiveEditPage } from './pages/LiveEditPage'
import { LoginPage } from './pages/LoginPage'
import { MappingsPage } from './pages/MappingsPage'
import { MarketingPage } from './pages/MarketingPage'
import { MockCheckoutPage } from './pages/MockCheckoutPage'
import { ModifyDonePage } from './pages/modify/ModifyDonePage'
import { ModifyEditPage } from './pages/modify/ModifyEditPage'
import { ModifyFiltersPage } from './pages/modify/ModifyFiltersPage'
import { ModifySelectTypePage } from './pages/modify/ModifySelectTypePage'
import { ModifyWizardProvider } from './modify/ModifyWizardProvider'
import { QuickCreatePage } from './pages/QuickCreatePage'
import { ReceiptsPage } from './pages/ReceiptsPage'
import { RegisterPage } from './pages/RegisterPage'
import { RulesPage } from './pages/RulesPage'
import { ForbiddenPage, NotFoundPage } from './pages/StatusPages'
import { ImportMappingPage } from './pages/import/ImportMappingPage'
import { ImportReviewPage } from './pages/import/ImportReviewPage'
import { ImportRunPage } from './pages/import/ImportRunPage'
import { ImportSelectFilePage } from './pages/import/ImportSelectFilePage'
import { ImportSelectTypePage } from './pages/import/ImportSelectTypePage'
import './styles/app.css'
import './styles/batchbooks.css'

export function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <CompanyProvider>
          <Routes>
            {/* Public surface. These carry the BatchBooks styling from
                styles/batchbooks.css, which also dresses the /app chrome. */}
            <Route path="/" element={<MarketingPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/get-started" element={<RegisterPage />} />

            {/* The offline payment gateway's stand-in for Stripe's hosted
                checkout. Signed-in but outside the /app shell, because the real
                one is a different origin with its own chrome — rendering it
                inside the app would look like an in-app confirmation rather
                than a payment. The two API routes behind it 404 unless the
                offline gateway is active. */}
            <Route
              path="/billing/mock-checkout"
              element={
                <RequireAuth>
                  <MockCheckoutPage />
                </RequireAuth>
              }
            />

            <Route
              path="/app"
              element={
                <RequireAuth>
                  <AppShell />
                </RequireAuth>
              }
            >
              <Route path="forbidden" element={<ForbiddenPage />} />

              {/* Reachable with no company, and therefore outside the guard: the
                  company list (where the OAuth callback lands) and the account
                  hub. A tenant with none is not redirected anywhere — the guard
                  renders the page they asked for behind a welcome dialog, so the
                  URL never moves out from under them. */}
              <Route path="companies" element={<CompaniesPage />} />
              <Route path="account" element={<AccountPage />} />

              {/* Everything below acts on a QuickBooks company, so a tenant with
                  none gets the welcome dialog over whatever page they reached. */}
              <Route element={<RequireCompany />}>
                <Route index element={<HomePage />} />

                {/* Import is the only chain wired end to end against the API. */}
                <Route
                  path="import"
                  element={
                    <RequireAuth capability="import">
                      <ImportWizardProvider />
                    </RequireAuth>
                  }
                >
                  <Route index element={<ImportSelectTypePage />} />
                  <Route path="upload" element={<ImportSelectFilePage />} />
                  <Route path="map" element={<ImportMappingPage />} />
                  <Route path="review" element={<ImportReviewPage />} />
                </Route>
                <Route
                  path="import/run/:jobId"
                  element={
                    <RequireAuth capability="import">
                      <ImportRunPage />
                    </RequireAuth>
                  }
                />

                <Route path="jobs" element={<JobsListPage />} />
                <Route path="jobs/:jobId" element={<JobDetailPage />} />
                <Route path="mappings" element={<MappingsPage />} />
                <Route path="rules" element={<RulesPage />} />
                {/* Settings is a dialog now, opened from the wizard steps that
                    need it. These paths are kept only so an old bookmark or a
                    link typed from memory lands somewhere useful instead of on
                    the 404 page. */}
                <Route path="settings" element={<Navigate to="/app" replace />} />
                <Route path="settings/*" element={<Navigate to="/app" replace />} />

                <Route
                  path="export"
                  element={
                    <RequireAuth capability="export">
                      <ExportWizardProvider />
                    </RequireAuth>
                  }
                >
                  <Route index element={<ExportSelectTypePage />} />
                  <Route path="filters" element={<ExportFiltersPage />} />
                  <Route path="fields" element={<ExportFieldsPage />} />
                  <Route path="done" element={<ExportDonePage />} />
                </Route>
                <Route
                  path="backup"
                  element={
                    <RequireAuth capability="export">
                      <BackupPage />
                    </RequireAuth>
                  }
                />

                <Route
                  path="delete"
                  element={
                    <RequireAuth capability="delete">
                      <DeleteWizardProvider />
                    </RequireAuth>
                  }
                >
                  <Route index element={<DeleteSelectSourcePage />} />
                  <Route path="filter" element={<DeleteFilterPage />} />
                  <Route path="review" element={<DeleteReviewPage />} />
                  <Route path="confirm" element={<DeleteConfirmPage />} />
                  <Route path="done" element={<DeleteDonePage />} />

                  {/* The two branches used to be separate pages; they are tabs on
                      step 2 now, so old links land there rather than 404. */}
                  <Route path="search" element={<Navigate to="/app/delete/filter" replace />} />
                  <Route path="references" element={<Navigate to="/app/delete/filter" replace />} />
                </Route>

                <Route
                  path="modify"
                  element={
                    <RequireAuth capability="import">
                      <ModifyWizardProvider />
                    </RequireAuth>
                  }
                >
                  <Route index element={<ModifySelectTypePage />} />
                  <Route path="filters" element={<ModifyFiltersPage />} />
                  <Route path="edit" element={<ModifyEditPage />} />
                  <Route path="done" element={<ModifyDonePage />} />
                </Route>
                <Route
                  path="live-edit"
                  element={
                    <RequireAuth capability="import">
                      <LiveEditPage />
                    </RequireAuth>
                  }
                />
                <Route
                  path="quick-create"
                  element={
                    <RequireAuth capability="import">
                      <QuickCreatePage />
                    </RequireAuth>
                  }
                />
                <Route
                  path="receipts"
                  element={
                    <RequireAuth capability="import">
                      <ReceiptsPage />
                    </RequireAuth>
                  }
                />
                <Route
                  path="bank-statements"
                  element={
                    <RequireAuth capability="import">
                      <BankStatementsPage />
                    </RequireAuth>
                  }
                />
                <Route path="automation" element={<AutomationPage />} />

                {/* Users and Billing are now tabs on the Account hub. Their old
                    routes redirect so existing links and bookmarks still land. */}
                <Route path="billing" element={<Navigate to="/app/account" replace />} />
                <Route path="users" element={<Navigate to="/app/account" replace />} />
                <Route
                  path="audit-log"
                  element={
                    <RequireAuth capability="admin">
                      <AuditLogPage />
                    </RequireAuth>
                  }
                />

                {/* The prototype's old hash routes, kept as redirects. */}
                <Route path="home" element={<Navigate to="/app" replace />} />
              </Route>
              <Route path="*" element={<NotFoundPage />} />
            </Route>

            {/* The signed-in app used to live at the root, so bookmarks like
                /jobs still exist in the wild. Anything unmatched at the top
                level is replayed under /app, where it either resolves or falls
                through to the 404 above. */}
            <Route path="*" element={<LegacyAppRedirect />} />
          </Routes>
        </CompanyProvider>
      </AuthProvider>
    </BrowserRouter>
  )
}

function LegacyAppRedirect() {
  const location = useLocation()
  return <Navigate to={`/app${location.pathname}${location.search}`} replace />
}
